import React, { useCallback, useEffect, useRef, useState } from 'react';

import { api, getHttpErrorStatus, logHttpError } from '../../services/api';
import type {
  AcquireSecurityOperation,
  ReleaseSecurityOperation,
  SecurityOperation,
} from '../../hooks/useSecurityOperationMutex';
import type {
  AcknowledgeRecoveryCodes,
  PendingRecoveryCodes,
  PublishRecoveryCodes,
} from './recoveryCodeMemory';

interface SetupResponse {
  secret: string;
  otpauth_uri: string;
}

interface TwoFactorStatus {
  enabled: boolean;
  setup_pending: boolean;
  recovery_codes_remaining: number;
}

interface TwoFactorSetupProps {
  securityOperation: SecurityOperation | null;
  acquireSecurityOperation: AcquireSecurityOperation;
  releaseSecurityOperation: ReleaseSecurityOperation;
  pendingRecoveryCodes: PendingRecoveryCodes | null;
  publishRecoveryCodes: PublishRecoveryCodes;
  acknowledgeRecoveryCodes: AcknowledgeRecoveryCodes;
}

type Confirmation = 'regenerate' | 'disable' | null;

const emptyStatus: TwoFactorStatus = {
  enabled: false,
  setup_pending: false,
  recovery_codes_remaining: 0,
};

const TwoFactorSetup: React.FC<TwoFactorSetupProps> = ({
  securityOperation,
  acquireSecurityOperation,
  releaseSecurityOperation,
  pendingRecoveryCodes,
  publishRecoveryCodes,
  acknowledgeRecoveryCodes,
}) => {
  const [factorStatus, setFactorStatus] = useState<TwoFactorStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [setup, setSetup] = useState<SetupResponse | null>(null);
  const [setupCode, setSetupCode] = useState('');
  const [confirmation, setConfirmation] = useState<Confirmation>(null);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const irreversibleBusy = securityOperation?.kind === 'two-factor-irreversible';
  const sessionRevokeBusy = securityOperation?.kind === 'session-revoke';
  const operationLocked = securityOperation !== null;

  const refreshFactorStatus = useCallback(async () => {
    try {
      const response = await api.get<TwoFactorStatus>('/security/2fa/status');
      if (mountedRef.current) setFactorStatus(response.data);
    } catch (statusError: unknown) {
      logHttpError('Ошибка загрузки состояния 2FA', statusError);
      if (mountedRef.current) setError('Не удалось загрузить состояние 2FA');
    } finally {
      if (mountedRef.current) setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void refreshFactorStatus();
    return () => {
      mountedRef.current = false;
    };
  }, [refreshFactorStatus]);

  const beginSetup = async () => {
    if (operationLocked) return;
    setLoading(true);
    setError(null);
    setStatusMessage(null);
    try {
      const response = await api.post<SetupResponse>('/security/2fa/setup');
      if (mountedRef.current) {
        setSetup(response.data);
        setFactorStatus({ enabled: false, setup_pending: true, recovery_codes_remaining: 0 });
      }
    } catch (setupError: unknown) {
      logHttpError('Ошибка настройки 2FA', setupError);
      if (mountedRef.current) setError('Не удалось начать настройку 2FA');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const enable = async (event: React.FormEvent) => {
    event.preventDefault();
    if (operationLocked || !setupCode.trim()) return;
    const operationToken = acquireSecurityOperation('two-factor-irreversible');
    if (operationToken === null) return;

    setLoading(true);
    setError(null);
    try {
      const response = await api.post<{ enabled: boolean; recovery_codes: string[] }>(
        '/security/2fa/enable',
        { code: setupCode.trim() },
      );
      publishRecoveryCodes(operationToken, response.data.recovery_codes);
      if (mountedRef.current) {
        setFactorStatus({
          enabled: response.data.enabled,
          setup_pending: false,
          recovery_codes_remaining: response.data.recovery_codes.length,
        });
        setSetup(null);
        setSetupCode('');
        void refreshFactorStatus();
      }
    } catch (enableError: unknown) {
      logHttpError('Ошибка включения 2FA', enableError);
      if (mountedRef.current) {
        setError(
          getHttpErrorStatus(enableError) === 400
            ? 'Неверный код 2FA'
            : 'Не удалось включить 2FA',
        );
      }
    } finally {
      if (mountedRef.current) setLoading(false);
      releaseSecurityOperation(operationToken);
    }
  };

  const confirmAction = async (event: React.FormEvent) => {
    event.preventDefault();
    if (operationLocked || !confirmation || !confirmationCode.trim()) return;
    const regenerating = confirmation === 'regenerate';
    const operationToken = regenerating
      ? acquireSecurityOperation('two-factor-irreversible')
      : null;
    if (regenerating && operationToken === null) return;

    setLoading(true);
    setError(null);
    setStatusMessage(null);
    try {
      if (regenerating && operationToken !== null) {
        const response = await api.post<{ recovery_codes: string[] }>(
          '/security/2fa/recovery/regenerate',
          { code: confirmationCode.trim() },
        );
        publishRecoveryCodes(operationToken, response.data.recovery_codes);
        if (mountedRef.current) {
          setFactorStatus((current) => ({
            ...(current ?? emptyStatus),
            enabled: true,
            setup_pending: false,
            recovery_codes_remaining: response.data.recovery_codes.length,
          }));
        }
      } else {
        await api.post('/security/2fa/disable', { code: confirmationCode.trim() });
        if (mountedRef.current) {
          setFactorStatus(emptyStatus);
          setStatusMessage('Двухфакторная аутентификация отключена');
        }
      }
      if (mountedRef.current) {
        setConfirmation(null);
        setConfirmationCode('');
        void refreshFactorStatus();
      }
    } catch (actionError: unknown) {
      logHttpError('Ошибка управления 2FA', actionError);
      if (mountedRef.current) {
        setError(
          getHttpErrorStatus(actionError) === 400
            ? 'Неверный код 2FA или резервный код'
            : 'Не удалось изменить настройки 2FA',
        );
      }
    } finally {
      if (mountedRef.current) setLoading(false);
      if (operationToken !== null) releaseSecurityOperation(operationToken);
    }
  };

  const enabled = factorStatus?.enabled ?? false;

  return (
    <section
      className="bg-[#15171c] p-4 sm:p-6 rounded-2xl border border-gray-800"
      aria-labelledby="two-factor-title"
      aria-label="Управление двухфакторной аутентификацией"
      aria-busy={operationLocked}
    >
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h4 id="two-factor-title" className="text-lg font-bold text-white">Двухфакторная аутентификация</h4>
          <p className="text-sm text-gray-500 mt-1">Код из приложения или одноразовый резервный код при входе.</p>
        </div>
        {!statusLoading && factorStatus && !setup && !enabled && (
          <button
            type="button"
            onClick={() => void beginSetup()}
            disabled={loading || operationLocked}
            className="shrink-0 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-sm transition disabled:opacity-50"
          >
            {loading
              ? 'Настройка...'
              : factorStatus?.setup_pending
                ? 'Начать настройку заново'
                : 'Настроить 2FA'}
          </button>
        )}
      </div>

      {statusLoading && <p role="status" className="text-gray-500 text-sm mt-4">Загрузка состояния 2FA...</p>}
      {irreversibleBusy && (
        <p role="status" className="text-amber-300 text-sm mt-4">
          {confirmation === 'regenerate' ? 'Обновление резервных кодов...' : 'Включение 2FA...'} Не закрывайте настройки.
        </p>
      )}
      {sessionRevokeBusy && (
        <p role="status" className="text-amber-300 text-sm mt-4">
          Дождитесь завершения операции с активной сессией.
        </p>
      )}
      {factorStatus?.setup_pending && !setup && !enabled && !statusLoading && (
        <p className="text-amber-300 text-sm mt-4">Настройка 2FA не завершена</p>
      )}
      {error && <p role="alert" className="text-red-400 text-sm mt-4">{error}</p>}
      {statusMessage && <p role="status" className="text-green-400 text-sm mt-4">{statusMessage}</p>}

      {setup && (
        <form onSubmit={enable} className="mt-5 space-y-4">
          <div className="bg-[#0f1115] border border-gray-700 rounded-xl p-4">
            <p className="text-gray-400 text-sm">Добавьте ключ в приложение-аутентификатор:</p>
            <code className="block text-blue-300 font-mono text-sm mt-2 break-all select-all">{setup.secret}</code>
            <a href={setup.otpauth_uri} className="inline-block text-blue-400 hover:text-blue-300 text-sm mt-3 underline">
              Открыть в приложении-аутентификаторе
            </a>
          </div>
          <label htmlFor="totp-enable-code" className="block text-xs text-gray-500 uppercase tracking-wider font-bold">Код из приложения</label>
          <input
            id="totp-enable-code"
            aria-label="Код из приложения"
            autoComplete="one-time-code"
            inputMode="numeric"
            value={setupCode}
            onChange={(event) => setSetupCode(event.target.value)}
            disabled={loading || operationLocked}
            className="w-full bg-[#0f1115] border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-blue-500 outline-none disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={loading || operationLocked || !setupCode.trim()}
            className="px-6 py-2 bg-green-600 hover:bg-green-500 text-white rounded-xl font-bold transition disabled:opacity-50"
          >
            {irreversibleBusy ? 'Включение 2FA...' : 'Включить 2FA'}
          </button>
        </form>
      )}

      {enabled && !setup && (
        <div className="mt-5">
          <p className="text-green-400 text-sm font-bold">✓ 2FA включена</p>
          <p className="text-gray-500 text-xs mt-1 mb-4">
            Осталось резервных кодов: {factorStatus?.recovery_codes_remaining ?? 0}
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => {
                if (operationLocked) return;
                setConfirmation('regenerate');
                setConfirmationCode('');
                setError(null);
              }}
              disabled={loading || operationLocked}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-bold text-sm transition disabled:opacity-50"
            >
              Обновить резервные коды
            </button>
            <button
              type="button"
              onClick={() => {
                if (operationLocked) return;
                setConfirmation('disable');
                setConfirmationCode('');
                setError(null);
              }}
              disabled={loading || operationLocked}
              className="px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded-xl font-bold text-sm transition disabled:opacity-50"
            >
              Отключить 2FA
            </button>
          </div>
        </div>
      )}

      {confirmation && (
        <form onSubmit={confirmAction} className="mt-5 bg-[#0f1115] border border-gray-700 rounded-xl p-4 space-y-3">
          <p className="text-white text-sm font-bold">
            {confirmation === 'disable'
              ? 'Подтвердите отключение двухфакторной аутентификации'
              : 'Подтвердите обновление резервных кодов'}
          </p>
          <p className="text-gray-500 text-xs">Введите текущий код 2FA или неиспользованный резервный код.</p>
          <label htmlFor="two-factor-confirmation-code" className="sr-only">
            {confirmation === 'disable' ? 'Код для отключения 2FA' : 'Код для обновления резервных кодов'}
          </label>
          <input
            id="two-factor-confirmation-code"
            aria-label={confirmation === 'disable' ? 'Код для отключения 2FA' : 'Код для обновления резервных кодов'}
            autoComplete="one-time-code"
            value={confirmationCode}
            onChange={(event) => setConfirmationCode(event.target.value)}
            disabled={loading || operationLocked}
            className="w-full bg-[#15171c] border border-gray-700 rounded-xl px-4 py-3 text-white focus:border-blue-500 outline-none disabled:opacity-50"
          />
          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={loading || operationLocked || !confirmationCode.trim()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold text-sm transition disabled:opacity-50"
            >
              {irreversibleBusy
                ? 'Обновление кодов...'
                : confirmation === 'disable'
                  ? 'Подтвердить отключение'
                  : 'Подтвердить обновление кодов'}
            </button>
            <button
              type="button"
              onClick={() => { setConfirmation(null); setConfirmationCode(''); }}
              disabled={loading || operationLocked}
              className="px-4 py-2 text-gray-400 hover:text-white rounded-xl text-sm transition disabled:opacity-50"
            >
              Отмена
            </button>
          </div>
        </form>
      )}

      {pendingRecoveryCodes && (
        <div role="alert" className="mt-5 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
          <p className="text-amber-300 font-bold text-sm">Сохраните резервные коды сейчас</p>
          <p className="text-amber-200/70 text-xs mt-1">
            Каждый код работает только один раз. После подтверждения эти коды больше не будут показаны.
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4 font-mono text-sm text-white" aria-label="Резервные коды">
            {pendingRecoveryCodes.codes.map((code) => <li key={code} className="bg-[#0f1115] rounded-lg px-3 py-2 select-all">{code}</li>)}
          </ul>
          <button
            type="button"
            onClick={() => acknowledgeRecoveryCodes(pendingRecoveryCodes.token)}
            className="mt-4 px-4 py-2 bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 rounded-xl font-bold text-sm transition"
          >
            Я сохранил коды
          </button>
        </div>
      )}
    </section>
  );
};

export default TwoFactorSetup;
